/**
 * The colour language of the game. Pure numbers — imported by data definitions
 * and the renderer alike.
 *
 * Rule: the world is ash and bone. Only units, banners and ability FX are
 * allowed to be saturated. If a ground colour here has chroma, it is wrong.
 */

export const C = {
  // Ground / world
  ash: 0x2a2724,
  ashDark: 0x1b1917,
  ashDeep: 0x14120f,
  ashLight: 0x3a3733,
  slate: 0x232629,
  bone: 0xd9cfbc,
  boneDim: 0xa89e8c,
  boneDeep: 0x6d675e,
  parchment: 0xc9bda2,

  // Accents
  oxblood: 0x6e1f22,
  oxbloodLight: 0x9c3336,
  gold: 0xc8a44c,
  goldPale: 0xe6cf92,
  goldDeep: 0x8a6c26,
  plague: 0x7d9436,
  plagueLight: 0xa8c257,
  indigo: 0x2b2a55,
  indigoLight: 0x4a4788,
  ember: 0xe0742a,
  emberLight: 0xf5a54f,
  emberDeep: 0x8c3d12,

  // Semantic
  hpGood: 0x8fae4a,
  hpMid: 0xd8a53a,
  hpBad: 0xa8302f,
  friendlyRim: 0xf0c77e,
  hostileRim: 0x92abe0,
  will: 0x7fa8d4,
  souls: 0xb9d3e6,

  // Terrain base tints.
  //
  // Deliberately dark. The ground has to sit a long way below the units in
  // value or a 300-unit brawl turns to soup — this was the single biggest
  // legibility fix in the whole renderer, and it is worth more than any amount
  // of detail on the tiles themselves.
  terrAsh: 0x211f1c,
  terrRuin: 0x2a2723,
  terrForest: 0x1c221a,
  terrStone: 0x2e2d2a,
  terrIron: 0x30291f,
  terrMarsh: 0x1e221d,
  terrRiver: 0x172128,
  /** The channel. Darker and colder than the ford, so depth reads at a glance. */
  terrDeep: 0x0e181f,
  /** Timber over water. Warmer than the road, because it is planks and not dirt. */
  terrBridge: 0x4a3d2c,
  terrRoad: 0x322d26,
  // Darker than anything else on the ground: a ditch reads as a hole.
  terrDitch: 0x171512,
  // A leyline is the one tile allowed a hue. Still dark enough to sit under the
  // units — the ground never competes — but cold where everything else is warm,
  // which is all it takes to find one across a map.
  terrLeyline: 0x1b1f2a,
} as const;

export type ColorKey = keyof typeof C;

/** Multiply a packed RGB colour by a scalar, clamping each channel. */
export function shade(color: number, mul: number): number {
  const r = Math.min(255, Math.max(0, Math.round(((color >> 16) & 0xff) * mul)));
  const g = Math.min(255, Math.max(0, Math.round(((color >> 8) & 0xff) * mul)));
  const b = Math.min(255, Math.max(0, Math.round((color & 0xff) * mul)));
  return (r << 16) | (g << 8) | b;
}

/** Linear blend between two packed RGB colours. */
export function mix(a: number, b: number, t: number): number {
  const it = 1 - t;
  const r = Math.round(((a >> 16) & 0xff) * it + ((b >> 16) & 0xff) * t);
  const g = Math.round(((a >> 8) & 0xff) * it + ((b >> 8) & 0xff) * t);
  const bl = Math.round((a & 0xff) * it + (b & 0xff) * t);
  return (r << 16) | (g << 8) | bl;
}

export function toCss(color: number, alpha = 1): string {
  const r = (color >> 16) & 0xff;
  const g = (color >> 8) & 0xff;
  const b = color & 0xff;
  return alpha >= 1 ? `rgb(${r},${g},${b})` : `rgba(${r},${g},${b},${alpha})`;
}

export function toHex(color: number): string {
  return '#' + (color >>> 0).toString(16).padStart(6, '0');
}
